<template>
  <div class="space-y-3">
    <div class="space-y-1">
      <!-- Deliberately says nothing about whether the record was saved: the parent heading owns
           that, and it distinguishes "never confirmed" from "confirmed, zero effect". -->
      <p class="m-0 text-xs text-gray-500">Values exactly as parsed from the file.</p>
      <p v-if="preview.vendorName" class="m-0 text-sm text-gray-700">
        Detected vendor:
        <span class="font-medium text-gray-900">{{ preview.vendorName }}</span>
      </p>
      <p v-if="preview.warnings.length > 0" class="m-0 text-xs text-amber-700">{{ preview.warnings.length }} warning(s): {{ preview.warnings.join('; ') }}</p>
    </div>

    <div v-if="comparison" class="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
      <span v-if="decreaseCount > 0" class="font-semibold text-red-700">↓ {{ decreaseCount }} week{{ decreaseCount === 1 ? '' : 's' }} would lose capacity</span>
      <span v-if="increaseCount > 0" class="font-semibold text-emerald-700">↑ {{ increaseCount }} week{{ increaseCount === 1 ? '' : 's' }} would gain</span>
      <span v-if="newCount > 0">{{ newCount }} week{{ newCount === 1 ? '' : 's' }} not on the calendar yet</span>
      <span v-if="decreaseCount === 0 && increaseCount === 0 && newCount === 0">Every week in this file already matches the calendar.</span>
      <!-- The parse stores no snapshot of the calendar as it was, so this is necessarily a
           comparison against today — say so rather than let an old parse imply otherwise. -->
      <span class="text-gray-500">Compared with the calendar as it is now.</span>
    </div>
    <p v-else-if="comparisonError" class="m-0 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">{{ comparisonError }}</p>
    <p v-else-if="comparisonLoading" class="m-0 px-3 py-2 text-xs text-gray-500">Comparing with the calendar…</p>

    <p v-if="preview.productionLines.length === 0" class="m-0 py-6 text-center text-sm text-gray-500">This file contains no production lines.</p>

    <div v-else class="max-h-[32rem] overflow-x-auto overflow-y-auto">
      <table class="min-w-full text-sm">
        <thead>
          <tr>
            <th class="table-th sticky top-0 z-10 bg-gray-50">Metric</th>
            <th v-for="week in preview.weeks" :key="week.weekNumber" class="table-th sticky top-0 z-10 bg-gray-50 text-right">W{{ week.weekNumber }}</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <template v-for="(line, lineIndex) in preview.productionLines" :key="`${line.lineLabel}-${lineIndex}`">
            <tr>
              <td :colspan="preview.weeks.length + 1" class="px-4 pb-1 pt-3 text-sm font-semibold text-gray-900">
                {{ line.lineLabel }}
                <span class="font-normal text-gray-500">
                  · {{ line.vendorCode ?? 'unknown vendor' }} · {{ line.terminationTypeCode }}
                  <template v-if="line.capacityFactor !== null">· factor {{ line.capacityFactor }}</template>
                </span>
              </td>
            </tr>
            <tr v-for="(metric, metricIndex) in line.metrics" :key="`${lineIndex}-${metricIndex}-${metric.key}`">
              <td class="px-4 py-1.5 font-medium text-gray-700">{{ metric.name }}</td>
              <td
                v-for="(week, weekIndex) in preview.weeks"
                :key="week.weekNumber"
                class="px-4 py-1.5 text-right tabular-nums"
                :class="cellClass(metric.key, lineIndex, weekIndex)"
              >
                <div>{{ format(metric.values[weekIndex] ?? null) }}</div>
                <!-- Only the capacity metric has a calendar counterpart; line qty / input /
                     remained are file-only and get no comparison. -->
                <div v-if="metric.key === 'capacity'" class="text-xs font-normal">{{ deltaLabel(lineIndex, weekIndex) }}</div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import type { CapacityAllocationMap, CalendarEntry } from '@/types/capacityCalendar';
  import type { CapacityMetricKey, CapacityPlanParseResult } from '@/types/capacityImport';
  import { buildParsedComparison } from '@/utils/importPreview';
  import type { ParsedWeekComparison } from '@/utils/importPreview';

  const props = defineProps<{
    preview: CapacityPlanParseResult;
    // Absent until the calendar loads (or if it fails) — the table then renders the parsed
    // values alone, which is still the whole point of this pane.
    entries?: CalendarEntry[] | null;
    allocations?: CapacityAllocationMap | null;
    comparisonLoading?: boolean;
    comparisonError?: string | null;
  }>();

  const comparison = computed<Map<string, ParsedWeekComparison> | null>(() =>
    props.entries && props.allocations ? buildParsedComparison(props.preview, props.entries, props.allocations) : null
  );

  const decreaseCount = computed(() => countByStatus('decrease'));
  const increaseCount = computed(() => countByStatus('increase'));
  const newCount = computed(() => countByStatus('new'));

  const numberFormat = new Intl.NumberFormat();
  const signedFormat = new Intl.NumberFormat(undefined, { signDisplay: 'always' });

  function format(value: number | null): string {
    return value === null ? '—' : numberFormat.format(value);
  }

  function countByStatus(status: ParsedWeekComparison['status']): number {
    if (!comparison.value) return 0;
    return [...comparison.value.values()].filter((entry) => entry.status === status).length;
  }

  function at(lineIndex: number, weekIndex: number): ParsedWeekComparison | undefined {
    return comparison.value?.get(`${lineIndex}:${weekIndex}`);
  }

  function deltaLabel(lineIndex: number, weekIndex: number): string {
    const entry = at(lineIndex, weekIndex);
    if (!entry) return '';
    if (entry.status === 'new') return 'new';
    if (entry.status === 'unresolved' || entry.status === 'same' || entry.delta === null) return '';
    return `${entry.status === 'decrease' ? '↓' : '↑'} ${signedFormat.format(entry.delta)}`;
  }

  function cellClass(metricKey: CapacityMetricKey, lineIndex: number, weekIndex: number): string {
    if (metricKey !== 'capacity') return '';
    const entry = at(lineIndex, weekIndex);
    if (entry?.status === 'decrease') return 'bg-red-50 font-semibold text-red-700';
    if (entry?.status === 'increase') return 'text-emerald-700';
    return '';
  }
</script>
